// src/pages/Video/VideoListPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Container,
    Grid,
    Loader,
    SimpleGrid,
    Title,
    Text,
    Stack,
    Drawer,
    Group,
    Button,
} from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { useBodyClass } from '@/hooks';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
    loadVideos,
    selectVideosStatus,
    selectVideosError,
    selectUpcomingVideos,
    makeSelectVideosByMembers,
    type FilterMode,
} from '@/features/videos/videosSlice';

import { fetchMembers, selectMembersStatus } from '@/features/members/membersSlice';
import { selectParticipantsForFilter } from '@/features/members/membersSelectors';

import type { ParticipantOption } from '@/components/Video/VideoFiltersSidebar/VideoFiltersSidebar';
import { VideoFiltersSidebar } from '@/components/Video/VideoFiltersSidebar/VideoFiltersSidebar';
import { SearchBar } from '@/components/SearchBar/SearchBar';
import { VideoItemsList } from '@/components/Video/VideoItemsList/VideoItemsList';
import { VideoListPagination } from '@/components/Video/VideoItemsList/VideoListPagination';
import { VideoItem } from '@/components/Video/VideoItem';

import classes from './VideoPages.module.scss';

import type { VideoRecord, VideoItemData } from '@/types/video';
import { toItem } from '@/features/videos/videoNormalize';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';

const parseCsvParam = (v: string | null): string[] =>
    v?.trim() ? v.split(',').map((s) => s.trim()).filter(Boolean) : [];

const toCsvParam = (arr: string[]) => (arr.length ? arr.join(',') : undefined);

export const VideoListPage: React.FC = () => {

    useBodyClass(classes.videosPage);
    const [opened, { toggle, close }] = useDisclosure(false);
    const dispatch = useAppDispatch();

    const status = useAppSelector(selectVideosStatus);
    const error = useAppSelector(selectVideosError);

    // Members (для фильтров)
    const membersStatus = useAppSelector(selectMembersStatus);
    const participantOptions = useAppSelector(selectParticipantsForFilter) as ParticipantOption[];

    // Premieres
    const premieresRecords = useAppSelector(selectUpcomingVideos); // VideoRecord[]
    const premieres: VideoItemData[] = useMemo(
        () => premieresRecords.map(toItem),
        [premieresRecords]
    );

    // URL params
    const [params, setParams] = useSearchParams();
    const urlMembers = parseCsvParam(params.get('members'));
    const urlMode = (params.get('mode') as FilterMode) || 'any';
    const urlQuery = params.get('q') ?? '';

    // Local UI state
    const [selectedMembers, setSelectedMembers] = useState<string[]>(urlMembers);
    const [mode, setMode] = useState<FilterMode>(urlMode);
    const [query, setQuery] = useState<string>(urlQuery);

    const [searchResults, setSearchResults] = useState<VideoItemData[]>([]);
    const [currentItems, setCurrentItems] = useState<VideoItemData[]>([]);
    const [currentSearch, setCurrentSearch] = useState<string>(urlQuery);

    // Load datasets
    useEffect(() => {
        if (status === 'idle') void dispatch(loadVideos());
    }, [status, dispatch]);

    useEffect(() => {
        if (membersStatus === 'idle') void dispatch(fetchMembers());
    }, [membersStatus, dispatch]);

    // Фильтрация по участникам (на уровне Record)
    const selectVideosFilteredByMembers = useMemo(
        () => makeSelectVideosByMembers(selectedMembers, mode),
        [selectedMembers, mode]
    );
    const allFilteredRecords = useAppSelector(selectVideosFilteredByMembers) as VideoRecord[];

    // Исключаем премьеры из общего списка
    const baseFilteredRecords = useMemo(
        () =>
            allFilteredRecords.filter(
                (v) =>
                    !v.upcoming &&
                    ((v.releasedTs ?? v.releaseTs ?? 0) <= Date.now() ||
                        (v.released == null && v.release_date == null))
            ),
        [allFilteredRecords]
    );

    // Приводим к UI-типу
    const baseFiltered: VideoItemData[] = useMemo(
        () => baseFilteredRecords.map(toItem),
        [baseFilteredRecords]
    );

    // Значения поиска по умолчанию
    useEffect(() => {
        if (baseFiltered.length && !query) setSearchResults(baseFiltered);
    }, [baseFiltered, query]);

    // Итоговый список: поиск или базовая фильтрация
    const finalFiltered: VideoItemData[] = useMemo(
        () => (currentSearch ? searchResults : baseFiltered),
        [currentSearch, searchResults, baseFiltered]
    );

    // Sync state → URL
    useEffect(() => {
        const next = new URLSearchParams(params);
        const membersCsv = toCsvParam(selectedMembers);
        if (membersCsv) next.set('members', membersCsv);
        else next.delete('members');
        if (mode && mode !== 'any') next.set('mode', mode);
        else next.delete('mode');
        if (query && query.trim()) next.set('q', query.trim());
        else next.delete('q');

        if (next.toString() !== params.toString()) setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMembers, mode, query]);

    // URL → state (навигация назад/вперёд)
    useEffect(() => {
        const m = parseCsvParam(params.get('members'));
        const md = (params.get('mode') as FilterMode) || 'any';
        const q = params.get('q') ?? '';
        setSelectedMembers(m);
        setMode(md);
        setQuery(q);
        setCurrentSearch(q);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.toString()]);

    const hasFilters =
        selectedMembers.length > 0 || mode !== 'any' || query.trim().length > 0;

    const showPremieres = premieres.length > 0 && !hasFilters;

    const handleResetAll = useCallback(() => {
        setSelectedMembers([]);
        setMode('any');
        setQuery('');
        setCurrentSearch('');
        setSearchResults(baseFiltered);
    }, [baseFiltered]);

    // Блок премьер как отдельная мемо-вставка без хуков внутри
    const premieresBlock = useMemo(() => {
        if (!showPremieres) return null;
        return (
            <Box mb="lg">
                <Title order={4} mb="sm">
                    Upcoming Episodes
                </Title>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                    {premieres.map((item) => (
                        <VideoItem key={item.id} item={item} />
                    ))}
                </SimpleGrid>
            </Box>
        );
    }, [showPremieres, premieres]);

    // Единая ветка рендера, без ранних return
    const content = useMemo(() => {
        if (error) {
            return <div>Error: {String(error)}</div>;
        }
        if (status === 'loading' && finalFiltered.length === 0) {
            return <Loader />;
        }
        return (
            <>
                <Box mb="md">
                    <Title order={2}>Podcasts</Title>
                    <Text c="dimmed">Videos with IFBB Pro</Text>
                </Box>
                {/* Premieres */}
                <Box component="section">
                    {status === 'loading' && premieres.length === 0 && (
                        <Box mb="lg">
                            <Loader />
                        </Box>
                    )}
                    {error && (
                        <Box mb="lg">
                            <Text c="red">Failed to load premieres: {String(error)}</Text>
                        </Box>
                    )}
                    {premieresBlock}
                </Box>

                <Grid gutter="xl">
                    {/* Sidebar на десктопе */}
                    <Grid.Col span={{ base: 12, md: 3 }} visibleFrom="md">
                        <VideoFiltersSidebar
                            participants={participantOptions}
                            selected={selectedMembers}
                            onChangeSelected={setSelectedMembers}
                            mode={mode}
                            onChangeMode={setMode}
                            onResetAll={handleResetAll}
                            showReset={hasFilters}
                        />
                    </Grid.Col>

                    {/* Контент */}
                    <Grid.Col span={{ base: 12, md: 9 }}>
                        <Group justify="space-between" mb="sm" hiddenFrom="md">
                            <div />
                            <Button
                                onClick={toggle}
                                leftSection={<IconAdjustmentsHorizontal size={18} />}
                                variant="light"
                                aria-label="Open Filters"
                            >
                                Filters
                            </Button>
                        </Group>

                        <SearchBar<VideoItemData>
                            videos={baseFiltered}
                            value={query}
                            onChangeValue={(v) => {
                                setQuery(v);
                                setCurrentSearch(v);
                            }}
                            setSearchResults={setSearchResults}
                        />

                        <VideoItemsList items={currentItems} />

                        <VideoListPagination
                            data={finalFiltered}
                            itemsPerPage={25}
                            setCurrentItems={setCurrentItems}
                            currentSearch={currentSearch}
                        />
                    </Grid.Col>
                </Grid>
            </>
        );
    }, [
        error,
        status,
        finalFiltered.length,
        premieres.length,
        premieresBlock,
        participantOptions,
        selectedMembers,
        mode,
        handleResetAll,
        hasFilters,
        toggle,
        baseFiltered,
        query,
        currentSearch,
        setSearchResults,
        currentItems,
    ]);

    return (
        <section>
            <Container>{content}</Container>

            {/* Мобильный фильтр — вне memo, но без хуков внутри */}
            <Drawer
                opened={opened}
                onClose={close}
                position="left"
                size="100%"
                padding="md"
                hiddenFrom="md"
                aria-label="Filter pros"
                overlayProps={{ opacity: 0.55, blur: 2 }}
                lockScroll
                withCloseButton
                withinPortal
            >
                <Stack gap="lg" component="nav" role="navigation" aria-label="Filter pros">
                    <VideoFiltersSidebar
                        participants={participantOptions}
                        selected={selectedMembers}
                        onChangeSelected={setSelectedMembers}
                        mode={mode}
                        onChangeMode={setMode}
                        onResetAll={handleResetAll}
                        showReset={hasFilters}
                    />
                    <Group justify="space-between" mt="md">
                        {hasFilters ? (
                            <Button variant="subtle" onClick={handleResetAll}>
                                Reset
                            </Button>
                        ) : (
                            <span />
                        )}
                        <Button onClick={close} variant="filled">
                            Apply
                        </Button>
                    </Group>
                </Stack>
            </Drawer>
        </section>
    );
};
